import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Search, X } from 'lucide-react';

interface SmartSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
  color: var(--text-tertiary);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 48px;
  padding-right: 48px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  font-size: 16px;
  background: var(--surface-color);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const SmartSearchBar: React.FC<SmartSearchBarProps> = ({
  value,
  onChange,
  placeholder = 'חיפוש...',
  debounceMs = 300
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const debouncedOnChange = useCallback(
    (newValue: string) => {
      const handler = setTimeout(() => {
        onChange(newValue);
      }, debounceMs);
      return () => clearTimeout(handler);
    },
    [onChange, debounceMs]
  );

  useEffect(() => {
    if (localValue !== value) {
      const cleanup = debouncedOnChange(localValue);
      return cleanup;
    }
  }, [localValue, value, debouncedOnChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <SearchContainer>
      <SearchInputWrapper>
        <SearchIcon>
          <Search size={20} />
        </SearchIcon>
        <SearchInput
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
        />
        {localValue && (
          <ClearButton onClick={handleClear} title="נקה חיפוש">
            <X size={18} />
          </ClearButton>
        )}
      </SearchInputWrapper>
    </SearchContainer>
  );
};

export default SmartSearchBar;
